import { useState, type FC, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { Tree } from '@minoru/react-dnd-treeview'
import classnames from 'classnames'
import { EmptyState } from 'shared/ui'
import {
  type IDropOptions,
  type IInboxTreeCombine,
  type INodeModel,
  type TreeRootInboxes,
} from 'entities/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useConversationMenuContext } from 'pages/conversations/ui/ConversationMenu/context'
import { MenuCardTree } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuCardTree/MenuCardTree'
import { MenuCardPreviewTree } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuCardPreviewTree/MenuCardPreviewTree'
import { MenuPlaceholderTree } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuPlaceholderTree/MenuPlaceholderTree'
import { SkeletonMenuSection } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/SkeletonMenuSection/SkeletonMenuSection.tsx'
import styles from './styles.module.scss'
import { MenuTitle } from './MenuTitle/MenuTitle'

type IMenuSectionSmartViewsTreeProps = {
  root: TreeRootInboxes
  handleDrop: (
    items: INodeModel<IInboxTreeCombine>[],
    options: IDropOptions<IInboxTreeCombine>,
    root: TreeRootInboxes
  ) => void
}

export const MenuSectionSmartViewsTree: FC<IMenuSectionSmartViewsTreeProps> = observer((props) => {
  const store = useConversationMenuContext()
  const { featureFlagsStore } = useEntitiesContext()
  const { itemsTreeSearch, hasItems, search, loading } = store.smartViewsMenuStore
  const { root, handleDrop } = props
  const [openCollapse, setOpenCollapse] = useState(true)
  const { inboxesStore } = useEntitiesContext()
  const { localInboxes } = inboxesStore
  const { currentSmartViewInboxId } = localInboxes
  const isEmpty = !itemsTreeSearch.length
  const enableCollapse = hasItems
  const enableSearch = hasItems
  const showList = !isEmpty && (openCollapse || search)
  const onClickItemTree = () => {}
  const onToggleCollapse = useCallback(() => {
    if (!enableCollapse) return

    setOpenCollapse(!openCollapse)
  }, [openCollapse, enableCollapse])

  if (!featureFlagsStore.smart_views) return null

  if (loading) {
    return <SkeletonMenuSection items={[40, 56, 64]} withTitle />
  }

  return (
    <div className={styles.wrap}>
      <MenuTitle
        enableSearch={enableSearch}
        enableCollapse={enableCollapse}
        openCollapse={openCollapse}
        toggleCollapse={onToggleCollapse}
      />

      {isEmpty && search && (
        <div className={styles.empty}>
          <EmptyState
            noIcon
            alignItems={'flex-start'}
            typographyProps={{
              variant: 'body-md-regular',
              color: 'var(--content-primary-disabled)',
              ariaLabel: 'search',
            }}
          />
        </div>
      )}

      <div
        className={classnames(styles.items, {
          [styles['items--hide']]: !openCollapse,
        })}
      >
        <Tree
          tree={itemsTreeSearch}
          rootId={root}
          render={(node, { isOpen, onToggle, hasChild }) => {
            const inboxId = node.data?.id
            const activeInbox = inboxId === currentSmartViewInboxId

            if (!showList && activeInbox) {
              return (
                <MenuCardTree
                  rootId={root}
                  node={node}
                  onToggle={onToggle}
                  isOpen={isOpen}
                  hasChild={hasChild}
                  onClickItemTree={onClickItemTree}
                />
              )
            }

            if (!showList) {
              return <></>
            }

            return (
              <MenuCardTree
                rootId={root}
                node={node}
                onToggle={onToggle}
                isOpen={isOpen}
                hasChild={hasChild}
                onClickItemTree={onClickItemTree}
              />
            )
          }}
          dragPreviewRender={(monitorProps) => {
            return <MenuCardPreviewTree node={monitorProps.item} />
          }}
          onDrop={(tree, options) => handleDrop(tree, options, root)}
          classes={{
            root: styles.treeRoot,
            draggingSource: styles.draggingSource,
            placeholder: styles.placeholderContainer,
            dropTarget: styles.dropTarget,
          }}
          sort={false}
          insertDroppableFirst={false}
          canDrop={(tree, { dragSource }) => {
            if (!dragSource?.data?.isSmartViewInbox) return false
          }}
          dropTargetOffset={10}
          placeholderRender={(node, { depth }) => <MenuPlaceholderTree node={node} depth={depth} />}
        />
      </div>
    </div>
  )
})
